/**
 * Ubiquiti UniFi catalog — full PoE switching lineup + UniFi Protect NVR
 * appliances + Cloud Gateways (UDM-Pro / SE / Pro Max) + UniFi Access
 * (intercoms, door/gate hubs, readers — customer request 2026-07-06).
 *
 * Switching/NVR/gateway specs verified against techspecs.ui.com on
 * 2026-05-15; the Access family on 2026-07-06. Source URL cited per
 * entry. Missing specs are left undefined rather than guessed.
 *
 * PoE marketing → IEEE mapping used throughout:
 *   PoE+   → 802.3at        (30 W / port)
 *   PoE++  → 802.3bt-Type3  (60 W / port)
 *   PoE+++ → 802.3bt-Type4  (90 W / port)
 *
 * UniFi Protect NVRs / UDM gateways set `onvif: false` — they only accept
 * Ubiquiti's proprietary Protect protocol cameras, not ONVIF.
 *
 * `hddBayMaxTb`, `codecs`, `raidLevels` are intentionally omitted across
 * the NVR entries; techspecs.ui.com does not publish those fields.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const UBIQUITI_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // UniFi Switching — Lite tier (entry, L2-managed, fanless)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubiquiti-usw-lite-8-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Lite-8-PoE',
    category: 'poe-switch',
    tagline: 'Compact 8-port Gigabit L2 switch with 4 PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-lite-8-poe',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 52,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'ubnt-usw-lite-16-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Lite-16-PoE',
    category: 'poe-switch',
    tagline: 'Fanless 16-port Gigabit L2 switch with 8 PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-lite-16-poe',
    specs: {
      totalPorts: 16,
      poePorts: 8,
      poeBudgetW: 45,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 32,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'ubnt-usw-16-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-16-PoE',
    category: 'poe-switch',
    tagline: 'Fanless 1U 16-port Gigabit L2 switch with 8 PoE+ ports and 2 SFP',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-16-poe',
    specs: {
      totalPorts: 18,
      poePorts: 8,
      poeBudgetW: 42,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '2× SFP 1G',
      switchingCapacityGbps: 36,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  {
    id: 'ubiquiti-usw-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-24-PoE',
    category: 'poe-switch',
    tagline: 'Fanless 1U 24-port Gigabit L2 switch with 16 PoE+ ports and 2 SFP',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 16,
      poeBudgetW: 95,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '2× SFP 1G',
      switchingCapacityGbps: 52,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  {
    id: 'ubiquiti-usw-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-48-PoE',
    category: 'poe-switch',
    tagline: 'Fanless 1U 48-port Gigabit L2 switch with 32 PoE+ ports and 4 SFP',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-48-poe',
    specs: {
      totalPorts: 52,
      poePorts: 32,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '4× SFP 1G',
      switchingCapacityGbps: 104,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Switching — Pro tier (L3-managed, PoE++ /PoE+++)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubnt-usw-pro-8-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-8-PoE',
    category: 'poe-switch',
    tagline: 'L3-managed 8-port PoE++ switch with 2 SFP+ 10G uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-8-poe',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'ubiquiti-usw-pro-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-24-PoE',
    category: 'poe-switch',
    tagline: 'L3-managed 1U 24-port switch with 8 PoE++ and 16 PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 400,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 88,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  {
    id: 'ubnt-usw-pro-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-48-PoE',
    category: 'poe-switch',
    tagline: 'L3-managed 1U 48-port switch with 8 PoE++ and 40 PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-48-poe',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 600,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'ubnt-usw-pro-max-16-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-Max-16-PoE',
    category: 'poe-switch',
    tagline: 'L3 Etherlighting switch with 4× 2.5G PoE++ and 12× 1G PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-max-16-poe',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 180,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 84,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  {
    id: 'ubnt-usw-pro-max-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-Max-24-PoE',
    category: 'poe-switch',
    tagline: 'L3 Etherlighting switch with 8× 2.5G PoE++ and 16× 1G PoE ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-max-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 400,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 112,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  {
    id: 'ubnt-usw-pro-max-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-Max-48-PoE',
    category: 'poe-switch',
    tagline: 'L3 Etherlighting 1U switch with 16× 2.5G PoE++ and 32× 1G PoE+ ports',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-max-48-poe',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 720,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 224,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'ubnt-usw-pro-hd-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-HD-24-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U switch with 22× 2.5G + 2× 10G PoE++ ports and 4× SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-hd-24-poe',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 600,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 230,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  {
    id: 'ubnt-usw-pro-xg-8-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-XG-8-PoE',
    category: 'poe-switch',
    tagline: 'Compact L3 switch with 8× 10G PoE++ ports and 2× SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-xg-8-poe',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 155,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 200,
      management: 'L3-managed',
      formFactor: 'desktop',
    },
  },

  {
    id: 'ubnt-usw-pro-xg-10-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-XG-10-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U switch with 10× 10G PoE+++ (90 W) ports and 2× SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-xg-10-poe',
    specs: {
      totalPorts: 12,
      poePorts: 10,
      poeBudgetW: 400,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 240,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  {
    id: 'ubnt-usw-pro-xg-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-XG-24-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U switch with 16× 10G + 8× 2.5G PoE+++ ports and 25G SFP28 uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-xg-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 720,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '2× SFP28 25G',
      switchingCapacityGbps: 460,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'ubnt-usw-pro-xg-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-XG-48-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U high-density switch with 48 PoE+++ ports and 4× 25G SFP28 uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-pro-xg-48-poe',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 1080,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '4× SFP28 25G',
      switchingCapacityGbps: 920,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Switching — Enterprise (2.5G access, L3-managed)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubiquiti-usw-enterprise-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Enterprise-24-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U switch with 24× 2.5G PoE+ ports and 2× SFP+ 10G uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-enterprise-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 400,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 124,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'ubnt-usw-enterprise-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Enterprise-48-PoE',
    category: 'poe-switch',
    tagline: 'L3 1U switch with 48× 2.5G PoE+ ports and 4× SFP+ 10G uplinks',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-enterprise-48-poe',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 720,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 160,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Switching — Flex / Industrial / Mission-Critical
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubnt-usw-flex-2-5g-8-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Flex-2.5G-8-PoE',
    category: 'poe-switch',
    tagline: 'Flex 8-port 2.5G PoE++ switch with 10G PoE+++ uplink',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-flex-2-5g-8-poe',
    specs: {
      totalPorts: 10,
      poePorts: 9,
      poeBudgetW: 196,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '1× SFP+ 10G',
      switchingCapacityGbps: 60,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'ubnt-usw-industrial',
    manufacturer: 'Ubiquiti',
    model: 'USW-Industrial',
    category: 'poe-switch',
    tagline: 'Hardened industrial 8-port PoE++ switch for extreme environments',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-industrial',
    specs: {
      totalPorts: 11,
      poePorts: 8,
      poeBudgetW: 430,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'industrial-DIN',
      fanless: true,
    },
  },

  {
    id: 'ubnt-usw-mission-critical',
    manufacturer: 'Ubiquiti',
    model: 'USW-Mission-Critical',
    category: 'poe-switch',
    tagline: 'UPS-backed 1U L3 switch with 4 PoE++ and 4 PoE+ ports for critical loads',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/switching/usw-mission-critical',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      switchingCapacityGbps: 18,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Protect NVRs — proprietary Protect protocol (NOT ONVIF)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubnt-unvr',
    manufacturer: 'Ubiquiti',
    model: 'UNVR',
    category: 'nvr-recorder',
    tagline: '1U UniFi Protect NVR with 4 HDD bays, up to 18× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/unvr',
    specs: {
      channels: 60,
      hddBays: 4,
      maxResolution: '4K',
      onvif: false,
      formFactor: '1U',
      networkPorts: '1× 10G SFP+, 1× 1G RJ45',
    },
  },

  {
    id: 'ubnt-unvr-pro',
    manufacturer: 'Ubiquiti',
    model: 'UNVR-Pro',
    category: 'nvr-recorder',
    tagline: '2U UniFi Protect NVR with 7 HDD bays, up to 24× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/unvr-pro',
    specs: {
      channels: 70,
      hddBays: 7,
      maxResolution: '4K',
      onvif: false,
      formFactor: '2U',
      networkPorts: '1× 10G SFP+, 1× 1G RJ45',
    },
  },

  {
    id: 'ubnt-unvr-g2',
    manufacturer: 'Ubiquiti',
    model: 'UNVR-G2',
    category: 'nvr-recorder',
    tagline: '1U UniFi Protect NVR with built-in AI analytics and HDMI Viewport',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/unvr-g2',
    specs: {
      channels: 60,
      hddBays: 4,
      maxResolution: '4K',
      onvif: false,
      formFactor: '1U',
      networkPorts: '1× 10G SFP+, 1× 2.5G RJ45',
    },
  },

  {
    id: 'ubnt-unvr-g2-pro',
    manufacturer: 'Ubiquiti',
    model: 'UNVR-G2-Pro',
    category: 'nvr-recorder',
    tagline: '2U UniFi Protect NVR with 8 HDD bays, AI analytics, up to 50× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/unvr-g2-pro',
    specs: {
      channels: 100,
      hddBays: 8,
      maxResolution: '4K',
      onvif: false,
      formFactor: '2U',
      networkPorts: '1× 10G SFP+, 1× 2.5G RJ45',
    },
  },

  {
    id: 'ubnt-unvr-instant',
    manufacturer: 'Ubiquiti',
    model: 'UNVR-Instant',
    category: 'nvr-recorder',
    tagline: 'Compact UniFi Protect NVR with integrated 6-port PoE switch + HDMI Viewport',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/unvr-instant',
    specs: {
      channels: 15,
      hddBays: 1,
      maxResolution: '4K',
      onvif: false,
      formFactor: 'desktop',
      networkPorts: '1× 1G uplink, 6× 1G PoE downlink, HDMI Viewport',
      poeInPorts: 6,
    },
  },

  {
    id: 'ubnt-envr',
    manufacturer: 'Ubiquiti',
    model: 'ENVR',
    category: 'nvr-recorder',
    tagline: '3U Enterprise UniFi Protect NVR with 16 HDD bays, up to 70× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cameras-nvrs/envr',
    specs: {
      channels: 210,
      hddBays: 16,
      maxResolution: '4K',
      onvif: false,
      formFactor: '3U',
      networkPorts: '2× 10G SFP+, 1× 10G RJ45',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Cloud Gateways with Protect (UDM family)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'ubnt-udm-pro',
    manufacturer: 'Ubiquiti',
    model: 'UDM-Pro',
    category: 'nvr-recorder',
    tagline: '1U Dream Machine Pro gateway with UniFi Protect, 1 HDD bay, up to 8× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cloud-gateways/udm-pro',
    specs: {
      channels: 24,
      hddBays: 1,
      maxResolution: '4K',
      onvif: false,
      formFactor: '1U',
      networkPorts: '1× 10G SFP+ WAN, 1× 1G RJ45 WAN, 8× 1G LAN, 2× 10G SFP+ LAN',
    },
  },

  {
    id: 'ubnt-udm-se',
    manufacturer: 'Ubiquiti',
    model: 'UDM-SE',
    category: 'nvr-recorder',
    tagline: '1U Dream Machine SE gateway with integrated 180 W PoE, NVR, up to 8× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cloud-gateways/udm-se',
    specs: {
      channels: 24,
      hddBays: 1,
      maxResolution: '4K',
      onvif: false,
      formFactor: '1U',
      networkPorts: '8× 1G RJ45, 1× 2.5G RJ45, 2× 10G SFP+',
      poeInPorts: 8,
      poeBudgetW: 180,
    },
  },

  {
    id: 'ubnt-udm-pro-max',
    manufacturer: 'Ubiquiti',
    model: 'UDM-Pro-Max',
    category: 'nvr-recorder',
    tagline: '1U Dream Machine Pro Max gateway with 2 HDD bays, up to 15× 4K cameras',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/cloud-gateways/udm-pro-max',
    specs: {
      channels: 50,
      hddBays: 2,
      maxResolution: '4K',
      onvif: false,
      formFactor: '1U',
      networkPorts: '8× 1G RJ45, 1× 2.5G RJ45, 2× 10G SFP+',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Access — Intercoms (outdoor door stations + indoor viewer)
  //
  // All specs verified against techspecs.ui.com on 2026-07-06. Managed by
  // the UniFi Access application (proprietary UniFi ecosystem). Both door
  // stations map to 'ip-sip' because third-party SIP server support is
  // officially documented (help.ui.com article 31504711305879,
  // 'Configuring Third-Party SIP Server with UniFi Access': UniFi Talk /
  // RingCentral / FreeSWITCH / FreePBX / etc.). NFC MIFARE Classic/DESFire
  // → 'mifare', touchscreen PIN entry → 'keypad' (house precedent); Face
  // Unlock, BLE and Apple/Google Wallet have no enum value → tagline only.
  // `callButtons` is omitted: neither station has a physical call button
  // (only a factory-reset button) — calls are placed on the touchscreen.
  // No onboard lock relay / Wiegand / RS-485 — unlock is done by a
  // separate UniFi Access hub (entries below).
  // ─────────────────────────────────────────────────────────────

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-intercom — 2 MP 1200×1600 stream from 8 MP optics, 7" 1024×600 touchscreen (1000 nits), NFC 13.56 MHz MIFARE Classic/DESFire + PIN + face unlock, PoE 13 W, IP65, -30 to 60 °C; third-party SIP per help.ui.com 31504711305879
  {
    id: 'ubiquiti-ua-intercom',
    manufacturer: 'Ubiquiti',
    model: 'UA-Intercom',
    category: 'intercom',
    tagline: 'Outdoor touchscreen door station — 2 MP camera, 7" display, NFC/PIN/face unlock, SIP, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-intercom',
    specs: {
      type: 'ip-sip', // third-party SIP support officially documented (help.ui.com 31504711305879)
      side: 'outdoor-station',
      cameraMP: 2, // 2 MP 1200×1600 stream (8 MP optics — do not list as 8 MP)
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare', 'keypad'], // NFC MIFARE Classic/DESFire; PIN on touchscreen
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g3-intercom — hardware identical to UA-Intercom; adds NFC ECP 2.0 → Apple/Google Wallet (Touch Pass); third-party SIP per help.ui.com 31504711305879
  {
    id: 'ubiquiti-ua-g3-intercom',
    manufacturer: 'Ubiquiti',
    model: 'UA-G3-Intercom',
    category: 'intercom',
    tagline: 'G3 outdoor touchscreen door station — 2 MP camera, 7" display, NFC + Apple/Google Wallet, PIN, face unlock, SIP, PoE, IP65',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g3-intercom',
    specs: {
      type: 'ip-sip', // third-party SIP support officially documented (help.ui.com 31504711305879)
      side: 'outdoor-station',
      cameraMP: 2, // 2 MP 1200×1600 stream (8 MP optics)
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare', 'keypad'], // NFC MIFARE Classic/DESFire + Wallet ECP 2.0; PIN on touchscreen
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-intercom-viewer — 5" 1280×720 portrait display (300 nits), mic + speaker, no camera, PoE 5 W, indoor only, 0–40 °C; NOT in the SIP support list → proprietary Access endpoint
  {
    id: 'ubiquiti-ua-intercom-viewer',
    manufacturer: 'Ubiquiti',
    model: 'UA-Intercom-Viewer',
    category: 'intercom',
    tagline: 'Indoor 5" answering display for UniFi Intercom / Reader Pro — mic + speaker, mobile unlock, PoE',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-intercom-viewer',
    specs: {
      type: 'ip-proprietary', // not listed in the third-party-SIP article — UniFi Access only
      side: 'indoor-monitor',
      monitorInches: 5,
      poe: true,
      // touchscreen unconfirmed on techspecs (no Screen Technology line) — not claimed
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Access — Door / Gate hubs (controllers)
  //
  // All hubs require the UniFi Access application on a UniFi console →
  // hostBus 'tcp-ip'. Readers attach over IP/PoE (proprietary), except
  // UA-Retrofit-Hub-2 which accepts 2× third-party Wiegand readers.
  // No hub publishes OSDP. The verbatim '(1) Emergency' input terminal on
  // UA-Hub-Door / UA-Hub-Gate / UA-Retrofit-Hub-2 / EAH-8 is mapped to
  // `fireAlarmRelease` (commented per entry); only UA-Hub-Door-Mini lacks
  // it. `maxUsersStandalone` is omitted — hubs are console-managed, not
  // standalone; user counts are stated in taglines instead.
  // ─────────────────────────────────────────────────────────────

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-hub-door — 1 door, 6,000 users, input terminals verbatim '(2) Door exit request (1) Door position (1) Emergency', dry + 12 V lock relays, PoE++ in, 4× PoE+ out (45 W agg), DIN rail
  {
    id: 'ubiquiti-ua-hub-door',
    manufacturer: 'Ubiquiti',
    model: 'UA-Hub-Door',
    category: 'door-controller',
    tagline: 'Single-door IP hub — 6,000 users, REX/DPS/emergency inputs, dry + 12 V lock relays, 4× PoE+ reader ports, DIN rail',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-hub-door',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip', // managed by UniFi Access application on a UniFi console
      fireAlarmRelease: true, // verbatim '(1) Emergency' input terminal
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-hub-door-mini — 1 door, input terminals only '(1) Door exit request (1) Door position' (no emergency input), dry 30 V/1 A + wet 12 V/1 A relays, PoE++ in, 2× PoE+ out (45 W agg), DIN rail
  {
    id: 'ubiquiti-ua-hub-door-mini',
    manufacturer: 'Ubiquiti',
    model: 'UA-Hub-Door-Mini',
    category: 'door-controller',
    tagline: 'Compact single-door IP hub — DPS + exit-request inputs, dry + 12 V lock relays, 2× PoE+ reader ports, DIN rail',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-hub-door-mini',
    specs: {
      doors: 1,
      hostBus: 'tcp-ip',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-hub-gate — '1 gate, 1 side door' → doors: 2; 2 operator relays 30 V/1 A + aux + 12 V side-door lock, gate/side-door position + exit-request + emergency inputs, 4× PoE+ out (55 W agg), -30 to 60 °C, DIN rail (device indoor only; outdoor via optional junction box)
  {
    id: 'ubiquiti-ua-hub-gate',
    manufacturer: 'Ubiquiti',
    model: 'UA-Hub-Gate',
    category: 'door-controller',
    tagline: 'Gate-operator + side-door IP hub — 2 operator relays, 12 V lock output, emergency input, 4× PoE+ reader ports, DIN rail',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-hub-gate',
    specs: {
      doors: 2, // techspecs: '1 gate, 1 side door'
      hostBus: 'tcp-ip',
      fireAlarmRelease: true, // verbatim '(1) Emergency' input terminal
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-retrofit-hub-2 — 2 doors, (2) third-party Wiegand / UniFi Retrofit reader inputs (only hub with Wiegand), 12/24 V DC (no PoE), 2× exit-request + 2× door-position + 1 emergency inputs, DIN rail
  {
    id: 'ubiquiti-ua-retrofit-hub-2',
    manufacturer: 'Ubiquiti',
    model: 'UA-Retrofit-Hub-2',
    category: 'door-controller',
    tagline: '2-door retrofit IP hub — 2× third-party Wiegand reader inputs, dry + 12/24 V lock relays, emergency input, 12/24 V DC powered, DIN rail',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-retrofit-hub-2',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      fireAlarmRelease: true, // verbatim '(1) Emergency' input terminal
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/eah-8 — TRUE capacity 8 doors / 10,000 users; schema doors union caps at 2 (same treatment as hid-aero-x1100 '2 doors, expandable to 64') — real count stated in tagline. 8 lock terminals (12 V or dry), 8 REX + 8 DPS + 1 emergency inputs, 8× PoE+ reader ports (190 W agg), 100–240 V AC + battery backup, wall mount
  {
    id: 'ubiquiti-eah-8',
    manufacturer: 'Ubiquiti',
    model: 'EAH-8',
    category: 'door-controller',
    tagline: '8-door enterprise access hub — 10,000 users, 8 lock relays, 8× PoE+ reader ports, emergency input, AC input + battery backup',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/eah-8',
    specs: {
      doors: 2, // SCHEMA LIMIT — actual supported doors: 8 (see tagline)
      hostBus: 'tcp-ip',
      fireAlarmRelease: true, // verbatim '(1) Emergency' input terminal
    },
  },

  // ─────────────────────────────────────────────────────────────
  // UniFi Access — Readers (NFC / biometric / retrofit OSDP)
  //
  // All readers are 13.56 MHz NFC with MIFARE Classic/DESFire (Plus/
  // Ultralight on some) → technology 'mifare' (house precedent). BLE
  // Mobile Unlock and Apple/Google Wallet Touch Pass have no enum →
  // tagline only. Non-retrofit readers speak proprietary UniFi over
  // IP/PoE — no Wiegand/OSDP → `protocol` omitted. Retrofit readers are
  // RS-485 OSDP v2 for third-party panels → protocol ['osdp'].
  // Reader Pros (camera + touchscreen two-way intercom) are kept in
  // 'access-reader' — reading is their primary function; camera/intercom
  // noted in taglines. `keypad: true` on Pro models = PIN entry via
  // touchscreen (commented); UA-G3-Flex has a physical keypad.
  // ─────────────────────────────────────────────────────────────

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g3-pro — 2 MP 1200×1600 camera (12 MP sensor), 4.7" touchscreen, two-way intercom, NFC MIFARE Classic/DESFire, Apple/Google Wallet, PIN, face unlock, BLE 4.2, PoE 6 W, IP55
  {
    id: 'ubiquiti-ua-g3-pro',
    manufacturer: 'Ubiquiti',
    model: 'UA-G3-Pro',
    category: 'access-reader',
    tagline: 'G3 Reader Pro — 2 MP camera, 4.7" touchscreen, two-way intercom, NFC + Apple/Google Wallet, PIN + face unlock, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g3-pro',
    specs: {
      technology: ['mifare'], // NFC 13.56 MHz, MIFARE Classic/DESFire
      outdoor: true,
      keypad: true, // PIN Access via 4.7" touchscreen (no physical keys)
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g3 — compact NFC reader + request-to-exit device, MIFARE Classic/DESFire, Apple/Google Wallet, BLE 4.1, handwave REX, PoE 5 W, IP55
  {
    id: 'ubiquiti-ua-g3',
    manufacturer: 'Ubiquiti',
    model: 'UA-G3',
    category: 'access-reader',
    tagline: 'Compact G3 NFC reader — Apple/Google Wallet, BLE mobile unlock, handwave request-to-exit, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g3',
    specs: {
      technology: ['mifare'], // NFC 13.56 MHz, MIFARE Classic/DESFire
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g3-flex — NFC reader with physical keypad ('with a keypad and Touch Pass support' per store), MIFARE Classic/DESFire + FeliCa, Apple/Google Wallet, PIN, BLE 4.1, PoE 5 W, IP55, -30 to 60 °C
  {
    id: 'ubiquiti-ua-g3-flex',
    manufacturer: 'Ubiquiti',
    model: 'UA-G3-Flex',
    category: 'access-reader',
    tagline: 'G3 NFC keypad reader — Apple/Google Wallet Touch Pass, PIN, BLE mobile unlock, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g3-flex',
    specs: {
      technology: ['mifare'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g3-fingerprint — NFC + fingerprint (300 templates) + keypad, MIFARE Classic/Plus/Ultralight/DESFire, Apple/Google Wallet, PIN, BLE 4.2, PoE 5 W, IP55, -30 to 50 °C. Store availability TBA Jul 2026 (pre-release)
  {
    id: 'ubiquiti-ua-g3-fingerprint',
    manufacturer: 'Ubiquiti',
    model: 'UA-G3-Fingerprint',
    category: 'access-reader',
    tagline: 'G3 NFC reader with fingerprint (300 templates) and keypad — Apple/Google Wallet, PIN, BLE, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g3-fingerprint',
    specs: {
      technology: ['mifare'], // fingerprint has no technology enum — tagline only
      outdoor: true,
      keypad: true,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g2-pro — previous gen, still sold; 2 MP 1200×1600 camera (12 MP sensor), 4.7" touchscreen, two-way intercom + doorbell, NFC MIFARE Classic/DESFire, PIN, BLE 4.2, PoE 6 W, IP55 (no Wallet on techspecs)
  {
    id: 'ubiquiti-ua-g2-pro',
    manufacturer: 'Ubiquiti',
    model: 'UA-G2-Pro',
    category: 'access-reader',
    tagline: 'G2 Reader Pro — 2 MP camera, 4.7" touchscreen, two-way intercom + doorbell, NFC, PIN, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g2-pro',
    specs: {
      technology: ['mifare'],
      outdoor: true,
      keypad: true, // PIN Access via touchscreen
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-g2 — previous gen; store shows Sold Out as of 2026-07-06 (not EOL-announced, techspecs page live); compact NFC reader, MIFARE Classic/DESFire + FeliCa, BLE 4.1, handwave REX, PoE 5 W, IP55
  {
    id: 'ubiquiti-ua-g2',
    manufacturer: 'Ubiquiti',
    model: 'UA-G2',
    category: 'access-reader',
    tagline: 'Compact G2 NFC reader — BLE mobile unlock, handwave request-to-exit, PoE, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-g2',
    specs: {
      technology: ['mifare'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-reader-lite — entry NFC reader, MIFARE Classic/Plus/Ultralight/DESFire, BLE 4.1, handwave REX, PoE 6 W, IP54, -20 to 40 °C, 'Indoor and Outdoor Use: Yes'
  {
    id: 'ubiquiti-ua-reader-lite',
    manufacturer: 'Ubiquiti',
    model: 'UA-Reader Lite',
    category: 'access-reader',
    tagline: 'Entry-level NFC reader — MIFARE Classic/Plus/Ultralight/DESFire, BLE mobile unlock, handwave request-to-exit, PoE, IP54',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-reader-lite',
    specs: {
      technology: ['mifare'],
      outdoor: true, // techspecs verbatim 'Indoor and Outdoor Use: Yes'
      keypad: false,
      ipRating: 'IP54',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-retrofit-reader — RS-485 OSDP v2 reader for existing third-party panels, 12 V DC (not PoE), MIFARE Classic/Plus/Ultralight/DESFire, Apple/Google Wallet, BLE 4.1, IP55, -30 to 50 °C
  {
    id: 'ubiquiti-ua-retrofit-reader',
    manufacturer: 'Ubiquiti',
    model: 'UA-Retrofit-Reader',
    category: 'access-reader',
    tagline: 'OSDP v2 retrofit NFC reader for third-party panels — RS-485, 12 V DC, Apple/Google Wallet, BLE mobile unlock, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-retrofit-reader',
    specs: {
      technology: ['mifare'],
      protocol: ['osdp'], // RS-485 with OSDP v2 — the exception in an otherwise proprietary-IP family
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-retrofit-reader-fingerprint — RS-485 OSDP v2, 12 V DC, fingerprint (300 templates) + keypad + PIN, MIFARE Classic/Plus/Ultralight/DESFire, Apple/Google Wallet, BLE 4.2, IP55. Store availability TBA Jul 2026 (pre-release)
  {
    id: 'ubiquiti-ua-retrofit-reader-fingerprint',
    manufacturer: 'Ubiquiti',
    model: 'UA-Retrofit-Reader-Fingerprint',
    category: 'access-reader',
    tagline: 'OSDP v2 retrofit reader with fingerprint (300 templates) and keypad — RS-485, 12 V DC, Apple/Google Wallet, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-retrofit-reader-fingerprint',
    specs: {
      technology: ['mifare'], // fingerprint has no technology enum — tagline only
      protocol: ['osdp'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP55',
    },
  },

  // SRC: https://techspecs.ui.com/unifi/door-access/ua-ultra + https://store.ui.com/us/en/category/door-access-hub/products/ua-ultra — 'access reader with a built-in hub for complete, single-door entry control'; modeled as access-reader with lockOutput (12 V DC 1 A relay) rather than door-controller: no DPS/REX inputs published and it is 'not compatible with standalone Access Hubs'. NFC MIFARE Classic/Plus/Ultralight/DESFire, BLE 4.2, 3,000 users, tamper switch, PoE+ 18 W, IP55, -30 to 40 °C
  {
    id: 'ubiquiti-ua-ultra',
    manufacturer: 'Ubiquiti',
    model: 'UA-Ultra',
    category: 'access-reader',
    tagline: 'All-in-one NFC reader with built-in single-door hub — 12 V 1 A lock relay, 3,000 users, BLE mobile unlock, PoE+, IP55',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/door-access/ua-ultra',
    specs: {
      technology: ['mifare'], // NFC 13.56 MHz, MIFARE Classic/Plus/Ultralight/DESFire
      outdoor: true,
      keypad: false,
      lockOutput: true, // built-in hub: powered lock relay 12 V DC, 1 A
      ipRating: 'IP55',
    },
  },
];
